import React, { useContext, useEffect, useRef, useState } from "react";
import { useContent } from "../content/ContentProvider";
import { RouteFadeContext } from "../App";
import StickyCta from "../components/StickyCta";
import { ArrowLeft, Volume2, VolumeX } from "lucide-react";

export default function WorksIntro() {
  const { fadeTo } = useContext(RouteFadeContext);
  const { worksIntro } = useContent();
  const WORKS_VIDEO = worksIntro.video;
  const cta = worksIntro.cta;
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true); // browsers block unmuted autoplay; we unmute on first interaction

  useEffect(() => {
    document.title = "Works · California Sexy";
  }, []);

  // Try to play with sound; if blocked, stay muted and unmute on first user interaction.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const tryUnmuted = async () => {
      v.muted = false;
      v.volume = 1;
      try {
        await v.play();
        setMuted(false);
      } catch {
        v.muted = true;
        setMuted(true);
        try {
          await v.play();
        } catch {}
      }
    };
    tryUnmuted();

    const onFirstInteract = async () => {
      const vid = videoRef.current;
      if (!vid) return;
      if (vid.muted) {
        vid.muted = false;
        vid.volume = 1;
        try {
          await vid.play();
          setMuted(false);
        } catch {}
      }
      window.removeEventListener("pointerdown", onFirstInteract);
      window.removeEventListener("keydown", onFirstInteract);
    };
    window.addEventListener("pointerdown", onFirstInteract);
    window.addEventListener("keydown", onFirstInteract);

    return () => {
      window.removeEventListener("pointerdown", onFirstInteract);
      window.removeEventListener("keydown", onFirstInteract);
    };
  }, []);

  const toggleMute = async () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.volume = 1;
    try {
      await v.play();
    } catch {}
    setMuted(v.muted);
  };

  const goHome = (e) => {
    e.preventDefault();
    fadeTo("/", 600);
  };

  return (
    <div
      className="page-enter"
      style={{
        position: "relative",
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        background: "#000",
      }}
    >
      <video
        ref={videoRef}
        src={WORKS_VIDEO}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          background: "#000",
        }}
      />

      {/* gentle bottom gradient so DEEEPER reads nicely */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)",
        }}
      />

      {/* Top bar */}
      <div
        style={{
          position: "absolute",
          top: "1.4rem",
          left: "1.6rem",
          right: "1.6rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 5,
        }}
      >
        <a
          href="/"
          onClick={goHome}
          className="font-math"
          style={{
            color: "#f4ece0",
            textDecoration: "none",
            fontSize: "0.95rem",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            opacity: 0.85,
          }}
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
          back
        </a>
        <button
          onClick={toggleMute}
          aria-label={muted ? "Unmute" : "Mute"}
          className="font-math"
          style={{
            background: "rgba(0,0,0,0.35)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(244,236,224,0.25)",
            color: "#f4ece0",
            padding: "0.4rem 0.7rem",
            borderRadius: "999px",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.45rem",
            cursor: "pointer",
            fontSize: "0.85rem",
          }}
        >
          {muted ? (
            <VolumeX size={15} strokeWidth={1.6} />
          ) : (
            <Volume2 size={15} strokeWidth={1.6} />
          )}
          {muted ? "sound off" : "sound on"}
        </button>
      </div>

      <StickyCta label={cta.label} target={cta.target} mode="absolute" />
    </div>
  );
}
